/**
 * <SessionDetailsLine> — la 2ᵉ ligne d'une séance : la pile de ses matières, puis « Maths ·
 * Physique-Chimie · avec Wajih · 1h » à l'encre secondaire (molecule).
 *
 * **Une séance a une seule anatomie** : une ligne horaire, puis cette ligne. La carte de la
 * prochaine séance (`<NextSessionHero>`), les lignes de « À venir » et de « Tous mes lives »
 * (`<SessionRow>`) et celles de « Quelle séance ? » (`<SessionPickerStep>`) la posent toutes —
 * mêmes repères, mêmes éléments, même ordre, même séparateur (`sessionDetailsLabel`). Rien n'est
 * calé à droite.
 *
 * La pile (`<SubjectBadges>`) et le texte (`subjectSummary`) comptent les matières avec la même
 * constante — toutes jusqu'à trois, puis deux et « +N ». Le texte tient deux lignes au plus, ne
 * se coupe qu'entre deux informations et s'ellipse au-delà. Sa hauteur de ligne est celle d'une
 * chip de matière (`subjectBadgeIconBox`) : la pile se cale sur la première ligne, et une ligne de
 * texte sans pile a la même hauteur qu'avec.
 *
 * `dimmed` estompe la pile, celle d'une séance où l'enfant ne sera pas ; le texte est déjà à
 * l'encre secondaire.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { subjectBadgeIconBox } from "../../atoms/SubjectBadge/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { SubjectBadges } from "../SubjectBadges"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { sessionDetailsLabel } from "../../../ports/primary/ui/formatters/liveSessionFormatter"

/** La boîte d'une chip de matière : la hauteur de chaque ligne du texte. */
export const DETAILS_LINE_BOX = subjectBadgeIconBox("sm")

export type SessionDetailsLineProps = {
    session: Pick<UILiveSession, "subjects" | "tutor" | "duration">
    /** Sans « avec Camille », quand la ligne du dessus nomme déjà le tuteur (l'en-tête du live). Défaut `true`. */
    withTutor?: boolean
    /** Estompe la pile de matières. Défaut `false`. */
    dimmed?: boolean
    /**
     * Ce que la ligne dit en plus, après les matières : la ligne du masteur nomme le type de cours
     * et l'école (`sessionDetailsLabel`). Rien par défaut.
     */
    extra?: string[]
    /**
     * La pile et les noms des matières. Défaut `true` ; `false` chez le masteur quand chaque élève
     * écrit les siennes, sur sa propre ligne (`<TutorSessionRoster>`).
     */
    withSubjects?: boolean
    testID?: string
}

export function SessionDetailsLine(props: SessionDetailsLineProps) {
    const { session, withTutor = true, dimmed = false, extra, withSubjects = true, testID } = props

    return (
        <Row gap="2" align="start" testID={testID}>
            {withSubjects && <SubjectBadges subjects={session.subjects} showLabel={false} dimmed={dimmed} />}
            <Text size="text-sm" color="text-muted" numberOfLines={2} style={styles.label}>
                {sessionDetailsLabel(session, { tutor: withTutor, extra, subjects: withSubjects })}
            </Text>
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    label: {
        flex: 1,
        flexShrink: 1,
        lineHeight: DETAILS_LINE_BOX.height,
    },
}))
